import * as THREE from 'three/webgpu';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {EllipseControlPointRole, SketchPointKind} from './ISketchObject';
import type {SketchEllipseObject} from './SketchEllipseObject';
import {SketchPointObject} from './SketchPointObject';

/**
 * 椭圆长短轴标记点集合。
 * 说明：
 * 1. 四个点分别表达长轴正/反方向与短轴正/反方向；
 * 2. 部分椭圆的弧端点独立存在，不能复用这些轴标记点，否则拖拽语义会互相覆盖。
 */
export interface SketchEllipseAxisControlPoints {
    /** 长轴正方向标记点。 */
    majorPositive: SketchPointObject;
    /** 长轴反方向标记点。 */
    majorNegative: SketchPointObject;
    /** 短轴正方向标记点。 */
    minorPositive: SketchPointObject;
    /** 短轴反方向标记点。 */
    minorNegative: SketchPointObject;
}

/**
 * 创建椭圆长短轴标记点所需的几何定义。
 */
export interface SketchEllipseAxisControlPointInput {
    /** 椭圆圆心世界坐标。 */
    centerWorld: THREE.Vector3;
    /** 长半轴半径。 */
    radiusX: number;
    /** 短半轴半径。 */
    radiusY: number;
    /** 椭圆旋转角（弧度）。 */
    rotation: number;
}

/**
 * 椭圆控制点注册输入。
 */
export interface SketchEllipseControlPointRegistrationInput {
    /** 椭圆圆心控制点。 */
    centerPoint: SketchPointObject;
    /** 椭圆长短轴标记点集合。 */
    axisControlPoints: SketchEllipseAxisControlPoints;
}

/**
 * 椭圆控制点工厂。
 * 职责：
 * 1. 统一创建椭圆中心点、弧端点和长短轴标记点；
 * 2. 统一把长短轴标记点注册到 `SketchEllipseObject` 的拖拽语义中；
 * 3. 避免绘制、裁剪、引用构建链路各自重复计算轴端点。
 */
export class SketchEllipseControlPointFactory {
    /**
     * @param referencePlane 椭圆所属草图参考面。
     */
    public constructor(private readonly referencePlane: ReferencePlane) {
    }

    /**
     * 创建一个普通椭圆控制点。
     * @param position 控制点世界坐标。
     * @returns 控制点对象。
     */
    public createControlPoint(position: THREE.Vector3): SketchPointObject {
        return new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: this.referencePlane,
        });
    }

    /**
     * 按椭圆定义创建四个长短轴标记点。
     * @param input 椭圆轴向定义。
     * @returns 长短轴标记点集合。
     */
    public createAxisControlPoints(input: SketchEllipseAxisControlPointInput): SketchEllipseAxisControlPoints {
        return {
            majorPositive: this.createAxisControlPoint(this.resolveAxisWorldPoint(input, 0, 1)),
            majorNegative: this.createAxisControlPoint(this.resolveAxisWorldPoint(input, 0, -1)),
            minorPositive: this.createAxisControlPoint(this.resolveAxisWorldPoint(input, Math.PI / 2, 1)),
            minorNegative: this.createAxisControlPoint(this.resolveAxisWorldPoint(input, Math.PI / 2, -1)),
        };
    }

    /**
     * 创建使用雪花外观的椭圆长短轴端控制点。
     *
     * 仅轴端点使用雪花材质；椭圆圆心与部分椭圆弧起终点继续通过
     * {@link createControlPoint} 创建圆形控制点，避免扩大视觉变更范围。
     *
     * @param position 轴端点世界坐标。
     * @returns 雪花外观的轴端控制点。
     */
    private createAxisControlPoint(position: THREE.Vector3): SketchPointObject {
        return new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointDefaultSpriteMaterial(),
            referencePlane: this.referencePlane,
        });
    }

    /**
     * 将中心点和四个轴标记点注册到椭圆对象。
     * @param ellipse 目标椭圆对象。
     * @param input 控制点注册输入。
     */
    public registerCardinalControlPoints(
        ellipse: SketchEllipseObject,
        input: SketchEllipseControlPointRegistrationInput,
    ): void {
        const axisSnowflakeMaterial = PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
        [
            input.axisControlPoints.majorPositive,
            input.axisControlPoints.majorNegative,
            input.axisControlPoints.minorPositive,
            input.axisControlPoints.minorNegative,
        ].forEach((axisControlPoint) => {
            /**
             * 正向轴点可能复用已有线段端点；注册前统一覆盖为雪花，确保新建点与复用点
             * 使用同一视觉语义。圆心和部分椭圆弧端点不在此列表中，继续保持圆点。
             */
            axisControlPoint.material = axisSnowflakeMaterial;
        });
        ellipse.registerCardinalControlPoint(input.centerPoint, EllipseControlPointRole.Center);
        ellipse.registerCardinalControlPoint(input.axisControlPoints.majorPositive, EllipseControlPointRole.MajorPositive);
        ellipse.registerCardinalControlPoint(input.axisControlPoints.majorNegative, EllipseControlPointRole.MajorNegative);
        ellipse.registerCardinalControlPoint(input.axisControlPoints.minorPositive, EllipseControlPointRole.MinorPositive);
        ellipse.registerCardinalControlPoint(input.axisControlPoints.minorNegative, EllipseControlPointRole.MinorNegative);
    }

    /**
     * 收集已注册控制点，供调用方统一入场和同步材质快照。
     * @param input 控制点注册输入。
     * @returns 去重后的控制点列表。
     */
    public collectRegisteredControlPoints(input: SketchEllipseControlPointRegistrationInput): SketchPointObject[] {
        const pointMap = new Map<string, SketchPointObject>();
        [
            input.centerPoint,
            input.axisControlPoints.majorPositive,
            input.axisControlPoints.majorNegative,
            input.axisControlPoints.minorPositive,
            input.axisControlPoints.minorNegative,
        ].forEach((point) => {
            pointMap.set(point.uuid, point);
        });
        return Array.from(pointMap.values());
    }

    /**
     * 解析指定轴端点的世界坐标。
     * @param input 椭圆轴向定义。
     * @param axisAngleOffset 相对长轴的参数角偏移。
     * @param sign 正反方向标记。
     * @returns 轴端点世界坐标。
     */
    private resolveAxisWorldPoint(
        input: SketchEllipseAxisControlPointInput,
        axisAngleOffset: number,
        sign: 1 | -1,
    ): THREE.Vector3 {
        const localCenter = this.referencePlane.worldToLocal(input.centerWorld);
        const angle = input.rotation + axisAngleOffset;
        const radius = axisAngleOffset === 0 ? input.radiusX : input.radiusY;

        // 在参考面局部坐标中求轴端点，再交给 ReferencePlane 转回世界坐标，避免直接依赖全局 XY 平面。
        return this.referencePlane.localToWorld(
            localCenter.x + Math.cos(angle) * radius * sign,
            localCenter.y + Math.sin(angle) * radius * sign,
        );
    }
}
